import React, { useCallback, useEffect, useState } from 'react';
import {
  BeakerIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  FireIcon,
  HeartIcon,
  PauseIcon,
  PlayIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  diseaseWorkflowsApi,
  WorkflowDetail,
  WorkflowStep,
} from '../../api/diseaseWorkflows';
import { treatmentApi, CreateTreatmentRecordDto } from '../../api/treatments';

interface WorkflowDetailPanelProps {
  workflowId: string | null;
  onClose?: () => void;
  onRefresh: () => Promise<void>;
}

const STEP_TYPE_STYLES: Record<string, { bg: string; ring: string; label: string }> = {
  WARNING: { bg: 'bg-amber-50', ring: 'ring-amber-200', label: 'Assessment' },
  DIAGNOSTIC: { bg: 'bg-blue-50', ring: 'ring-blue-200', label: 'Diagnostic' },
  TREATMENT: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', label: 'Treatment' },
  MONITORING: { bg: 'bg-violet-50', ring: 'ring-violet-200', label: 'Monitoring' },
  PREVENTION: { bg: 'bg-cyan-50', ring: 'ring-cyan-200', label: 'Prevention' },
};

function severityColor(severity: string) {
  switch (severity) {
    case 'CRITICAL':
      return 'bg-red-100 text-red-800';
    case 'HIGH':
      return 'bg-orange-100 text-orange-800';
    case 'MEDIUM':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-cyan-100 text-cyan-800';
  }
}

function BulletList({ items, empty }: { items?: string[]; empty?: string }) {
  if (!items?.length) {
    return <p className="text-sm text-slate-500">{empty ?? 'None listed'}</p>;
  }
  return (
    <ul className="space-y-1">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500" />
          {item}
        </li>
      ))}
    </ul>
  );
}

function SectionCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
        <Icon className="h-4 w-4 text-cyan-600" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function TimelineStep({
  step,
  onComplete,
  completing,
}: {
  step: WorkflowStep & { isCurrent?: boolean };
  onComplete: (stepId: string) => void;
  completing: boolean;
}) {
  const style = STEP_TYPE_STYLES[step.stepType] ?? STEP_TYPE_STYLES.WARNING;

  return (
    <div
      className={`relative rounded-lg border p-4 ${
        step.completed
          ? 'border-green-200 bg-green-50/40'
          : step.isCurrent
            ? `border-cyan-300 ${style.bg} ring-2 ring-cyan-200`
            : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded px-2 py-0.5 text-xs font-medium ring-1 ${style.bg} ${style.ring}`}>
              {style.label}
            </span>
            {step.isCurrent && !step.completed && (
              <span className="rounded-full bg-cyan-600 px-2 py-0.5 text-xs font-medium text-white">
                Current step
              </span>
            )}
            {step.completed && (
              <span className="flex items-center gap-1 text-xs text-green-700">
                <CheckCircleIcon className="h-4 w-4" />
                Done
              </span>
            )}
          </div>
          <h4 className={`mt-2 font-medium ${step.completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
            {step.title}
          </h4>
          {step.description && <p className="mt-1 text-sm text-slate-600">{step.description}</p>}
        </div>
        {!step.completed && step.isCurrent && (
          <button
            type="button"
            disabled={completing}
            onClick={() => onComplete(step.id)}
            className="btn btn-primary shrink-0 text-xs"
          >
            Complete
          </button>
        )}
      </div>

      {(step.actions?.length || step.requiredEquipment?.length || step.estimatedDuration) && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {step.actions?.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Actions</p>
              <BulletList items={step.actions} />
            </div>
          )}
          {step.requiredEquipment?.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Equipment</p>
              <BulletList items={step.requiredEquipment} />
            </div>
          )}
        </div>
      )}
      {step.estimatedDuration != null && (
        <p className="mt-2 text-xs text-slate-500">
          Est. duration: {step.estimatedDuration >= 24 ? `${step.estimatedDuration}h` : `${step.estimatedDuration} min`}
        </p>
      )}
    </div>
  );
}

export default function WorkflowDetailPanel({ workflowId, onClose, onRefresh }: WorkflowDetailPanelProps) {
  const [detail, setDetail] = useState<WorkflowDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showTreatmentForm, setShowTreatmentForm] = useState(false);
  const [savingTreatment, setSavingTreatment] = useState(false);
  const [treatmentForm, setTreatmentForm] = useState({
    treatmentName: '',
    medicationName: '',
    dosage: '',
    frequency: 'ONCE_DAILY' as CreateTreatmentRecordDto['frequency'],
    administrationRoute: 'Oral',
    prescribedBy: '',
    instructions: '',
  });

  const loadDetail = useCallback(async () => {
    if (!workflowId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    try {
      const data = await diseaseWorkflowsApi.getDetail(workflowId);
      setDetail(data);
    } catch {
      toast.error('Failed to load workflow details');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleCompleteStep = async (stepId: string) => {
    if (!workflowId) return;
    setCompleting(true);
    try {
      await diseaseWorkflowsApi.completeStep(workflowId, stepId);
      toast.success('Step completed');
      await onRefresh();
      await loadDetail();
    } catch {
      toast.error('Failed to complete step');
    } finally {
      setCompleting(false);
    }
  };

  const handleStatusChange = async (action: 'pause' | 'resume' | 'cancel' | 'complete') => {
    if (!workflowId) return;
    try {
      if (action === 'pause') await diseaseWorkflowsApi.pause(workflowId);
      else if (action === 'resume') await diseaseWorkflowsApi.resume(workflowId);
      else if (action === 'cancel') await diseaseWorkflowsApi.cancel(workflowId);
      else await diseaseWorkflowsApi.complete(workflowId);
      toast.success(`Workflow ${action === 'complete' ? 'completed' : action + 'd'}`);
      await onRefresh();
      await loadDetail();
    } catch {
      toast.error('Action failed');
    }
  };

  const prefillTreatment = (medicationLabel: string) => {
    const name = medicationLabel.split('(')[0].trim();
    setTreatmentForm((prev) => ({
      ...prev,
      treatmentName: `${name} — ${detail?.workflow.diseaseName ?? 'workflow'}`,
      medicationName: name,
    }));
    setShowTreatmentForm(true);
  };

  const handleCreateTreatment = async () => {
    if (!detail || !treatmentForm.treatmentName || !treatmentForm.dosage || !treatmentForm.prescribedBy) {
      toast.error('Treatment name, dosage, and prescriber are required');
      return;
    }
    setSavingTreatment(true);
    try {
      await treatmentApi.create({
        birdId: detail.bird.id,
        treatmentName: treatmentForm.treatmentName,
        treatmentType: 'MEDICATION',
        medicationName: treatmentForm.medicationName || undefined,
        dosage: treatmentForm.dosage,
        frequency: treatmentForm.frequency,
        administrationRoute: treatmentForm.administrationRoute,
        startDate: new Date().toISOString(),
        prescribedBy: treatmentForm.prescribedBy,
        indication: detail.workflow.diseaseName,
        instructions: treatmentForm.instructions || undefined,
      });
      toast.success('Treatment record created');
      setShowTreatmentForm(false);
      setTreatmentForm({
        treatmentName: '',
        medicationName: '',
        dosage: '',
        frequency: 'ONCE_DAILY',
        administrationRoute: 'Oral',
        prescribedBy: '',
        instructions: '',
      });
      await loadDetail();
    } catch {
      toast.error('Failed to create treatment');
    } finally {
      setSavingTreatment(false);
    }
  };

  if (!workflowId) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-slate-50/50 p-8 text-center">
        <div>
          <ClipboardDocumentListIcon className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Select a workflow to view the clinical pathway and treatment plan</p>
        </div>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center rounded-lg border border-gray-200 bg-white p-8">
        <p className="text-sm text-slate-500">Loading workflow details…</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center rounded-lg border border-gray-200 bg-white p-8">
        <p className="text-sm text-slate-500">Workflow not found</p>
      </div>
    );
  }

  const { workflow, bird, timeline, treatments, diseaseCatalog, symptomTriggers } = detail;
  const progress = workflow.progressPercentage ?? 0;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-slate-50">
      <div className="border-b border-gray-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {workflow.severityLevel === 'CRITICAL' ? (
                <FireIcon className="h-5 w-5 text-red-500" />
              ) : null}
              <h2 className="text-lg font-semibold text-slate-900">{workflow.diseaseName}</h2>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityColor(workflow.severityLevel)}`}>
                {workflow.severityLevel}
              </span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{workflow.status}</span>
              {workflow.contagious && (
                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-800">Contagious</span>
              )}
              {workflow.zoonotic && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-800">Zoonotic</span>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-600">
              {bird.bandId ?? bird.id}
              {bird.species ? ` · ${bird.species}` : ''}
              {bird.cageNumber ? ` · Cage ${bird.cageNumber}` : ''}
            </p>
            {symptomTriggers.length > 0 && (
              <p className="mt-2 text-sm text-slate-600">
                <span className="font-medium">Triggered by:</span> {symptomTriggers.join(', ')}
              </p>
            )}
          </div>
          {onClose && (
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-200">
            <div className="h-full rounded-full bg-cyan-600 transition-all" style={{ width: `${progress}%` }} />
          </div>
          {workflow.nextStepDue && (
            <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
              <ClockIcon className="h-3.5 w-3.5" />
              Next due: {new Date(workflow.nextStepDue).toLocaleString()}
            </p>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {workflow.status === 'ACTIVE' && (
            <button type="button" onClick={() => handleStatusChange('pause')} className="btn btn-secondary text-xs">
              <PauseIcon className="h-3.5 w-3.5" />
              Pause
            </button>
          )}
          {workflow.status === 'PAUSED' && (
            <button type="button" onClick={() => handleStatusChange('resume')} className="btn btn-primary text-xs">
              <PlayIcon className="h-3.5 w-3.5" />
              Resume
            </button>
          )}
          {(workflow.status === 'ACTIVE' || workflow.status === 'PAUSED') && (
            <>
              <button type="button" onClick={() => handleStatusChange('complete')} className="btn btn-secondary text-xs">
                Mark complete
              </button>
              <button type="button" onClick={() => handleStatusChange('cancel')} className="btn btn-secondary text-xs text-red-700">
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <SectionCard title="Clinical pathway" icon={ClipboardDocumentListIcon}>
          <div className="space-y-3">
            {timeline.map((step) => (
              <TimelineStep
                key={step.id}
                step={step}
                onComplete={handleCompleteStep}
                completing={completing}
              />
            ))}
          </div>
        </SectionCard>

        {workflow.diagnosticProtocol && (
          <SectionCard title="Diagnostic plan" icon={BeakerIcon}>
            <p className="mb-2 text-xs text-slate-500">{workflow.diagnosticProtocol.timeline}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Tests</p>
                <BulletList items={workflow.diagnosticProtocol.tests} />
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Procedures</p>
                <BulletList items={workflow.diagnosticProtocol.procedures} />
              </div>
            </div>
          </SectionCard>
        )}

        {workflow.treatmentWorkflow && (
          <SectionCard title="Treatment protocol" icon={HeartIcon}>
            {workflow.treatmentWorkflow.durationGuidance && (
              <p className="mb-2 rounded-md bg-cyan-50 px-3 py-2 text-xs text-cyan-900">
                Duration: {workflow.treatmentWorkflow.durationGuidance}
              </p>
            )}
            <p className="mb-3 text-xs text-slate-500">{workflow.treatmentWorkflow.timeline}</p>

            {workflow.treatmentWorkflow.phases && workflow.treatmentWorkflow.phases.length > 0 && (
              <div className="mb-4 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Treatment phases</p>
                {workflow.treatmentWorkflow.phases.map((phase) => (
                  <div key={phase.phase} className="rounded-lg border border-gray-200 bg-slate-50/80 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium text-slate-900">{phase.phase}</p>
                      <span className="text-xs text-slate-500">{phase.duration}</span>
                    </div>
                    {phase.goals.length > 0 && (
                      <p className="mt-1 text-xs text-slate-600">
                        Goals: {phase.goals.join(' · ')}
                      </p>
                    )}
                    <div className="mt-2">
                      <BulletList items={phase.actions} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Medications</p>
                <BulletList items={workflow.treatmentWorkflow.medications} />
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Procedures</p>
                <BulletList items={workflow.treatmentWorkflow.procedures} />
              </div>
              {workflow.treatmentWorkflow.supportiveCare && workflow.treatmentWorkflow.supportiveCare.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Supportive care</p>
                  <BulletList items={workflow.treatmentWorkflow.supportiveCare} />
                </div>
              )}
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Follow-up</p>
                <BulletList items={workflow.treatmentWorkflow.followUp} />
              </div>
              {workflow.treatmentWorkflow.contraindications && workflow.treatmentWorkflow.contraindications.length > 0 && (
                <div className="sm:col-span-2">
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-red-600">Contraindications / cautions</p>
                  <BulletList items={workflow.treatmentWorkflow.contraindications} />
                </div>
              )}
              {workflow.treatmentWorkflow.nutritionSupport && workflow.treatmentWorkflow.nutritionSupport.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Nutrition</p>
                  <BulletList items={workflow.treatmentWorkflow.nutritionSupport} />
                </div>
              )}
              {workflow.treatmentWorkflow.environmentalManagement && workflow.treatmentWorkflow.environmentalManagement.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Environment</p>
                  <BulletList items={workflow.treatmentWorkflow.environmentalManagement} />
                </div>
              )}
              {workflow.treatmentWorkflow.speciesNotes && workflow.treatmentWorkflow.speciesNotes.length > 0 && (
                <div className="sm:col-span-2">
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Species notes</p>
                  <BulletList items={workflow.treatmentWorkflow.speciesNotes} />
                </div>
              )}
            </div>

            {(workflow.treatmentWorkflow.medications?.length > 0 || diseaseCatalog?.medicines.length) && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Quick-start treatment</p>
                <div className="flex flex-wrap gap-2">
                  {[...new Set([
                    ...(workflow.treatmentWorkflow.medications ?? []),
                    ...(diseaseCatalog?.medicines ?? []),
                  ])].slice(0, 6).map((med) => (
                    <button
                      key={med}
                      type="button"
                      onClick={() => prefillTreatment(med)}
                      className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-800 hover:bg-emerald-100"
                    >
                      + {med.split('(')[0].trim()}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={() => setShowTreatmentForm(!showTreatmentForm)}
                className="text-sm font-medium text-cyan-700 hover:text-cyan-900"
              >
                {showTreatmentForm ? 'Hide treatment form' : '+ Record treatment on bird'}
              </button>
              {showTreatmentForm && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <input
                    className="input text-sm sm:col-span-2"
                    placeholder="Treatment name *"
                    value={treatmentForm.treatmentName}
                    onChange={(e) => setTreatmentForm({ ...treatmentForm, treatmentName: e.target.value })}
                  />
                  <input
                    className="input text-sm"
                    placeholder="Medication"
                    value={treatmentForm.medicationName}
                    onChange={(e) => setTreatmentForm({ ...treatmentForm, medicationName: e.target.value })}
                  />
                  <input
                    className="input text-sm"
                    placeholder="Dosage *"
                    value={treatmentForm.dosage}
                    onChange={(e) => setTreatmentForm({ ...treatmentForm, dosage: e.target.value })}
                  />
                  <select
                    className="input text-sm"
                    value={treatmentForm.frequency}
                    onChange={(e) => setTreatmentForm({ ...treatmentForm, frequency: e.target.value as CreateTreatmentRecordDto['frequency'] })}
                  >
                    <option value="ONCE_DAILY">Once daily</option>
                    <option value="TWICE_DAILY">Twice daily</option>
                    <option value="EVERY_12_HOURS">Every 12 hours</option>
                    <option value="EVERY_8_HOURS">Every 8 hours</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="AS_NEEDED">As needed</option>
                  </select>
                  <input
                    className="input text-sm"
                    placeholder="Route (e.g. Oral)"
                    value={treatmentForm.administrationRoute}
                    onChange={(e) => setTreatmentForm({ ...treatmentForm, administrationRoute: e.target.value })}
                  />
                  <input
                    className="input text-sm sm:col-span-2"
                    placeholder="Prescribed by *"
                    value={treatmentForm.prescribedBy}
                    onChange={(e) => setTreatmentForm({ ...treatmentForm, prescribedBy: e.target.value })}
                  />
                  <textarea
                    className="input text-sm sm:col-span-2"
                    placeholder="Instructions"
                    rows={2}
                    value={treatmentForm.instructions}
                    onChange={(e) => setTreatmentForm({ ...treatmentForm, instructions: e.target.value })}
                  />
                  <button
                    type="button"
                    disabled={savingTreatment}
                    onClick={handleCreateTreatment}
                    className="btn btn-primary text-sm sm:col-span-2"
                  >
                    Save treatment record
                  </button>
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {treatments.active.length > 0 && (
          <SectionCard title="Active treatments on bird" icon={HeartIcon}>
            <div className="space-y-3">
              {treatments.active.map((t) => (
                <div key={t.id} className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-slate-900">{t.treatmentName}</p>
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs text-emerald-800 ring-1 ring-emerald-200">
                      {t.treatmentStatus}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    {t.medicationName ? `${t.medicationName} · ` : ''}
                    {t.dosage} · {t.frequency.replace(/_/g, ' ').toLowerCase()} · {t.administrationRoute}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Started {new Date(t.startDate).toLocaleDateString()}
                    {t.endDate ? ` · Ends ${new Date(t.endDate).toLocaleDateString()}` : ''}
                  </p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {workflow.monitoringSchedule && (
          <SectionCard title="Monitoring" icon={ClockIcon}>
            <p className="mb-2 text-sm font-medium text-slate-700">{workflow.monitoringSchedule.frequency}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Parameters</p>
                <BulletList items={workflow.monitoringSchedule.parameters} />
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Alert if</p>
                <BulletList items={workflow.monitoringSchedule.alerts} />
              </div>
            </div>
          </SectionCard>
        )}

        {(workflow.actionItems?.length > 0 || workflow.recommendations?.length > 0) && (
          <SectionCard title="Action items & recommendations" icon={ShieldCheckIcon}>
            <div className="grid gap-4 sm:grid-cols-2">
              {workflow.actionItems?.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Do now</p>
                  <BulletList items={workflow.actionItems} />
                </div>
              )}
              {workflow.recommendations?.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Recommendations</p>
                  <BulletList items={workflow.recommendations} />
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {diseaseCatalog && (
          <SectionCard title="Disease reference" icon={BeakerIcon}>
            {diseaseCatalog.medicines.length > 0 && (
              <div className="mb-3">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Reference medicines</p>
                <BulletList items={diseaseCatalog.medicines} />
              </div>
            )}
            {diseaseCatalog.diagnosis && (
              <p className="text-sm text-slate-600">
                <span className="font-medium">Diagnosis:</span> {diseaseCatalog.diagnosis}
              </p>
            )}
          </SectionCard>
        )}

        {workflow.notes && (
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Notes</p>
            <p className="mt-1 text-sm text-slate-700">{workflow.notes}</p>
          </section>
        )}
      </div>
    </div>
  );
}
